import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axiosInstance from '../../../../axiosConfig'; // Ensure this is correctly configured
import { X } from 'lucide-react';

const Banner = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Get all params from URL
  const queryParams = new URLSearchParams(location.search);
  
  // Extract all URL parameters
  const pageMetaFromUrl = queryParams.get('pageMeta') || 'Explore Sri Lanka Attractions';
  
  // Get all categoryId values (multiple possible)
  const categoryIds = queryParams.getAll('categoryId');
  
  // Get cityId
  const cityId = queryParams.get('cityId') || '';

  // State for multiple banners
  const [banners, setBanners] = useState([]);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Publishing state
  const [publishing, setPublishing] = useState(false);

  // Loading state for fetching viewMoreLinkPageId
  const [loadingPageId, setLoadingPageId] = useState(true);
  const [viewMoreLinkPageId, setViewMoreLinkPageId] = useState('');

  // Track if this is the first time modal is opened (to pre-fill only once)
  const [isFirstModalOpen, setIsFirstModalOpen] = useState(true);

  // Form fields for new banner
  const [formData, setFormData] = useState({
    mainTitle: '',
    subTitle: '',
    description: '',
    validFrom: '',
    validTo: '',
    alt: '',
    desktopImage: null, // File
    mobileImage: null, // File
  });

  // Fetch viewMoreLinkPageId based on pageMeta from URL
  useEffect(() => {
    const fetchViewMoreLinkPageId = async () => {
      if (!pageMetaFromUrl) {
        setLoadingPageId(false);
        return;
      }

      try {
        setLoadingPageId(true);
        const response = await axiosInstance.get(
          '/internal-api/v1/client-web/cms/custom-page/list',
          {
            params: {
              start: 0,
              length: '',
              'pageMeta[]': pageMetaFromUrl
            }
          }
        );

        const apiData = response.data;

        if (apiData.recordsTotal > 0 && apiData.data?.length > 0) {
          // Take the first matching record's id (as shown in your sample response)
          const page = apiData.data[0];
          setViewMoreLinkPageId(page.id.toString());
          console.log('Found viewMoreLinkPageId:', page.id);
        } else {
          console.warn('No custom page found for pageMeta:', pageMetaFromUrl);
        }
      } catch (error) {
        console.error('Error fetching viewMoreLinkPageId:', error);
      } finally {
        setLoadingPageId(false);
      }
    };

    fetchViewMoreLinkPageId();
  }, [pageMetaFromUrl]);

  // Pre-fill mainTitle with pageMeta only on first modal open
  useEffect(() => {
    if (isModalOpen && isFirstModalOpen) {
      setFormData((prev) => ({
        ...prev,
        mainTitle: pageMetaFromUrl,
      }));
      setIsFirstModalOpen(false);
    }
  }, [isModalOpen, isFirstModalOpen, pageMetaFromUrl]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: files[0],
    }));
  };

  const handleAddBanner = (e) => {
    e.preventDefault();
    // Add to banners list with unique id
    const newBanner = {
      ...formData,
      id: Date.now(),
      imageUrl: formData.desktopImage ? URL.createObjectURL(formData.desktopImage) : null, // For preview
    };
    setBanners((prev) => [...prev, newBanner]);

    // Reset form and close modal
    setFormData({
      mainTitle: '',
      subTitle: '',
      description: '',
      validFrom: '',
      validTo: '',
      alt: '',
      desktopImage: null,
      mobileImage: null,
    });
    setIsModalOpen(false);
  };

  const handleRemoveBanner = (bannerId) => {
    setBanners((prev) => prev.filter((b) => b.id !== bannerId));
  };

  const formatDateTime = (dateStr) => dateStr ? `${dateStr} 00:00:00` : '';

  const handlePublish = async () => {
    if (banners.length === 0) {
      alert('No banners added yet.');
      return;
    }

    if (!viewMoreLinkPageId) {
      alert('viewMoreLinkPageId not found. Please try again.');
      return;
    }

    if (!window.confirm(`Publish ${banners.length} banner(s)?`)) {
      return;
    }

    setPublishing(true);

    try {
      const formdata = new FormData();

      // Hardcoded fields as per your example request body
      formdata.append('pageCode', 'CUSTOM_DESTINATION_VIEW');
      formdata.append('name', 'test');
      formdata.append('displayOrder', '1');
      
      // Use fetched viewMoreLinkPageId from API
      formdata.append('viewMoreLinkPageId', viewMoreLinkPageId);
      
      // Use first banner's mainTitle as blockHeader and subTitle as subHeader
      const firstBanner = banners[0];
      formdata.append('blockHeader', firstBanner.mainTitle);
      formdata.append('subHeader', firstBanner.subTitle);
      
      formdata.append('blockContent', '');
      formdata.append('link', 'htt');
      
      // Use URL parameter for pageMeta
      formdata.append('pageMeta', pageMetaFromUrl);

      // Use first banner's validFrom/Till for group
      formdata.append('validFrom', formatDateTime(firstBanner.validFrom));
      formdata.append('validTill', formatDateTime(firstBanner.validTo));
      formdata.append('isPublic', '1');

      // bannerGroups[0]
      formdata.append('bannerGroups[0][displayOrder]', '2');
      formdata.append('bannerGroups[0][alignOrder]', '');

      // Loop over banners
      banners.forEach((banner, index) => {
        formdata.append(`bannerGroups[0][banners][${index}][mainTitle]`, banner.mainTitle);
        formdata.append(`bannerGroups[0][banners][${index}][subTitle]`, banner.subTitle);
        formdata.append(`bannerGroups[0][banners][${index}][shortDescription]`, '1'); // Hardcoded as in example
        formdata.append(`bannerGroups[0][banners][${index}][description]`, banner.description);
        formdata.append(`bannerGroups[0][banners][${index}][groupOrder]`, '2'); // Hardcoded
        formdata.append(`bannerGroups[0][banners][${index}][validFrom]`, formatDateTime(banner.validFrom));
        formdata.append(`bannerGroups[0][banners][${index}][validTill]`, formatDateTime(banner.validTo));
        formdata.append(`bannerGroups[0][banners][${index}][isActionButtonEnable]`, '0');
        formdata.append(`bannerGroups[0][banners][${index}][actionButtonModal][text]`, '');
        formdata.append(`bannerGroups[0][banners][${index}][actionButtonModal][hrefLink]`, '');
        formdata.append(`bannerGroups[0][banners][${index}][actionButtonModal][isNewTab]`, '');
        formdata.append(`bannerGroups[0][banners][${index}][actionButtonModal][validFrom]`, '');
        formdata.append(`bannerGroups[0][banners][${index}][actionButtonModal][validTill]`, '');

        // bannerContents[0]
        formdata.append(`bannerGroups[0][banners][${index}][bannerContents][0][alt]`, banner.alt);
        formdata.append(`bannerGroups[0][banners][${index}][bannerContents][0][isClickable]`, '');
        formdata.append(`bannerGroups[0][banners][${index}][bannerContents][0][displayOrder]`, '');
        formdata.append(`bannerGroups[0][banners][${index}][bannerContents][0][validFrom]`, '');
        formdata.append(`bannerGroups[0][banners][${index}][bannerContents][0][validTill]`, '');

        if (banner.mobileImage) {
          formdata.append(`bannerGroups[0][banners][${index}][bannerContents][0][mobileImageFile]`, banner.mobileImage);
        }
        if (banner.desktopImage) {
          formdata.append(`bannerGroups[0][banners][${index}][bannerContents][0][imageFile]`, banner.desktopImage);
        }
      });

      const response = await axiosInstance.post(
        '/internal-api/v1/client-web/cms/page/page-blocks/banner-group/new-group',
        formdata
      );

      if (response.status < 200 || response.status >= 300) {
        throw new Error('Failed to publish banner group');
      }

      alert('Banner group published successfully!');
      
      // Clear banners after successful publish
      setBanners([]);

      // ── Navigate to Attraction page with ALL parameters ──────────────────────────
      const params = new URLSearchParams();

      // 1. Pass viewMorePageId (derived from pageMeta lookup)
      if (viewMoreLinkPageId) {
        params.append('viewMorePageId', viewMoreLinkPageId);
      }

      // 2. Pass all categoryId values from original URL
      categoryIds.forEach(id => {
        params.append('categoryId', id);
      });

      // 3. Pass pageMeta from original URL
      if (pageMetaFromUrl) {
        params.append('pageMeta', pageMetaFromUrl);
      }

      // 4. Pass cityId if present in original URL
      if (cityId) {
        params.append('cityId', cityId);
      }

      // Navigate with all parameters
      navigate(`/Attraction?${params.toString()}`);
      // ───────────────────────────────────────────────────────────────────────────────

    } catch (error) {
      console.error('Error publishing banner group:', error);
      alert('Failed to publish banner group. Check console for details.');
    } finally {
      setPublishing(false);
    }
  };

  return (
    <>
      {/* Main Header Card (no default banner image) */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 relative">
        {/* Add Banner Button - positioned at top right */}
        <button
          onClick={() => setIsModalOpen(true)}
          className="absolute top-5 right-6 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium shadow-sm flex items-center gap-2"
          disabled={loadingPageId}
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4v16m8-8H4"
            />
          </svg>
          Add Banner
        </button>

        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {pageMetaFromUrl}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Discover Hidden Gems & Natural Wonders
            </p>
            {/* Display URL Parameters Info */}
            <div className="mt-3 text-xs text-gray-600 space-y-1">
              {loadingPageId ? (
                <p className="text-blue-600">Loading page information...</p>
              ) : (
                <>
                  {categoryIds.length > 0 && (
                    <p>Category IDs: {categoryIds.join(', ')}</p>
                  )}
                  {cityId && (
                    <p>City ID: {cityId}</p>
                  )}
                  {viewMoreLinkPageId && (
                    <p className="font-semibold text-green-600">
                      View More Link Page ID: {viewMoreLinkPageId}
                    </p>
                  )}
                  {!viewMoreLinkPageId && !loadingPageId && (
                    <p className="text-red-600">
                      Warning: Could not find View More Link Page ID
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handlePublish}
              disabled={publishing || banners.length === 0 || loadingPageId || !viewMoreLinkPageId}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {publishing ? 'Publishing...' : 'Publish'}
            </button>
          </div>
        </div>
      </div>

      {/* List of Added Banners */}
      <div className="mt-8 space-y-6">
        {banners.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>No banners added yet. Click "Add Banner" to start.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {banners.map((banner) => (
              <div key={banner.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 relative">
                <button
                  onClick={() => handleRemoveBanner(banner.id)}
                  className="absolute top-2 right-2 text-red-600 hover:text-red-800"
                >
                  <X size={20} />
                </button>
                <img
                  src={banner.imageUrl || 'https://via.placeholder.com/300x150?text=No+Image'}
                  alt={banner.alt}
                  className="w-full h-32 object-cover rounded-md mb-3"
                />
                <h3 className="text-lg font-semibold">{banner.mainTitle}</h3>
                <p className="text-sm text-gray-600">{banner.subTitle}</p>
                <p className="text-xs text-gray-500 mt-2">
                  Valid: {banner.validFrom} to {banner.validTo}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal for Adding Banner */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold text-gray-900">
                Create New Banner
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Add a promotional or featured banner
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleAddBanner} className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title *
                </label>
                <input
                  type="text"
                  name="mainTitle"
                  value={formData.mainTitle}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g. Sigiriya Rock Fortress Festival"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Subtitle
                </label>
                <input
                  type="text"
                  name="subTitle"
                  value={formData.subTitle}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g. Special 2026 Cultural Event"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Brief description or highlights..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Valid From *
                  </label>
                  <input
                    type="date"
                    name="validFrom"
                    value={formData.validFrom}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Valid Until *
                  </label>
                  <input
                    type="date"
                    name="validTo"
                    value={formData.validTo}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Alt Text
                </label>
                <input
                  type="text"
                  name="alt"
                  value={formData.alt}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Alt text for image"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Desktop Image *
                </label>
                <input
                  type="file"
                  name="desktopImage"
                  onChange={handleFileChange}
                  required
                  accept="image/*"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mobile Image *
                </label>
                <input
                  type="file"
                  name="mobileImage"
                  onChange={handleFileChange}
                  required
                  accept="image/*"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              {/* Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-5 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Add Banner
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default Banner;